import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import usePageScrollLock from '../../utils/usePageScrollLock';
import useMediaQuery from '../../utils/useMediaQuery';
import { IrisCircle } from '../profile/fields';
import { Icon } from './onboardingUi';
import profileService from '../../services/profileService';

/**
 * The profile photo gate — "Resume Notice v2.dc.html", the PROFILE GATE screen.
 *
 * Your own Portal Page with no photo on it is a blank name tag, and the page leads with a face by
 * design: a visitor who lands on it learns nothing. So the owner sees the page behind this and is
 * asked for the photo first. It gates the OWNER only — a visitor reading someone else's
 * photoless page is shown the page, not a lecture — and BACK TO EVENTS is always there, so it is
 * a door with a handle on both sides rather than a wall.
 *
 * "Has a photo" is `hasProfilePhoto` in `utils/onboardingModel`, the same rule step 1 enforces.
 *
 * **The photo is added right here.** The gate used to send you to `/onboard?next=<here>` for it,
 * on the reasoning that one place should know how to put a photo on a Portal Page — but that
 * answered a one-tap ask with a whole flow and a round trip back. The upload is the same two
 * calls onboarding's step 1 makes (`uploadGalleryPhoto`, then `avatar/from-gallery` centred), so
 * the rule about not forking the FIELDS still holds: there is no second uploader UI, just the
 * service calls behind this one button. `/onboard` is still a tap away for the rest of the page.
 *
 * `onAdded(profile)` hands the fresh profile to whoever mounted the gate; refreshing the user
 * context is the PAGE's job, not this modal's. The gate deliberately reads no context of its own —
 * it is portalled into `document.body` and mounts over a page that already holds the viewer, so a
 * second subscription here would only be another provider to stand up wherever it is rendered.
 */
export default function PhotoGate({ onAdded }) {
  const navigate = useNavigate();
  const location = useLocation();
  const desktop = useMediaQuery('(min-width: 1024px)');
  const phone = useMediaQuery('(max-width: 599px)');
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  usePageScrollLock(true);

  const here = `${location.pathname}${location.search || ''}`;

  const pick = () => { if (!busy) fileRef.current?.click(); };

  const onFile = async (file) => {
    setBusy(true);
    setError(null);

    const up = await profileService.uploadGalleryPhoto(file);
    if (!up.success || !up.data) {
      setError(up.error || 'That photo could not be uploaded. Try another.');
      setBusy(false);
      return;
    }

    // The cover IS the profile photo, cropped square at the centre — the same rule as step 1's
    // CONTINUE. Without this the photo would sit in the gallery and the gate would stay shut.
    const av = await profileService.setAvatarFromGallery(String(up.data.id), 50, 50);
    if (!av.success) {
      setError(av.error || 'The photo uploaded but could not be set as your profile photo.');
      setBusy(false);
      return;
    }

    setBusy(false);
    onAdded?.(av.data);
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="photo-gate-head"
      data-testid="photo-gate"
      className="pk pr-overlay-root"
      style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: desktop ? 'center' : 'flex-end', justifyContent: 'center', padding: desktop ? 24 : 0, boxSizing: 'border-box', background: 'rgba(0,0,0,.72)', color: '#fff' }}
    >
      <div style={{ width: '100%', maxWidth: 560, maxHeight: desktop ? 'calc(100vh - 48px)' : '92vh', overflowY: 'auto', background: '#111', borderRadius: desktop ? 28 : '28px 28px 0 0', boxShadow: 'inset 0 0 0 1px #1F1F1F, 0 24px 60px rgba(0,0,0,.6)', boxSizing: 'border-box' }}>
        {!desktop && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }} aria-hidden="true">
            <span style={{ width: 44, height: 5, borderRadius: 999, background: '#333' }} />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, padding: phone ? '18px 16px 22px' : '26px 28px 28px', textAlign: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, height: 30, padding: '0 14px', borderRadius: 999, background: '#161616', color: '#8A8A8A', fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>
            {Icon.lock}
            PROFILE · LOCKED
          </span>

          {/* The live FileList is copied to an array BEFORE `value` is reset — resetting it first
              empties the list the handler is still holding, and nothing uploads, silently. */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            data-testid="photo-gate-file"
            onChange={(e) => { const picked = Array.from(e.target.files || []); e.target.value = ''; if (picked.length) onFile(picked[0]); }}
            style={{ display: 'none' }}
          />

          <IrisCircle size={phone ? 148 : 176} onClick={pick} aria-label="Add your profile photo">
            <span style={{ position: 'absolute', inset: 5, borderRadius: '50%', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#fff' }}>
              {busy ? null : Icon.plus(30)}
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>{busy ? 'ADDING…' : 'TAP TO ADD'}</span>
            </span>
          </IrisCircle>

          <h2 id="photo-gate-head" style={{ margin: 0, fontSize: phone ? 25 : 30, fontWeight: 700, lineHeight: 1.12, letterSpacing: '-.01em', textTransform: 'none' }}>
            Add your profile photo to see your profile.
          </h2>
          <span style={{ maxWidth: 400, fontSize: 14, lineHeight: 1.6, color: '#8A8A8A' }}>
            Your profile leads with your face. People invite faces, not initials.
          </span>

          {error && (
            <span role="alert" data-testid="photo-gate-error" style={{ maxWidth: 400, fontSize: 12, lineHeight: 1.5, color: '#FF66C4' }}>
              {error}
            </span>
          )}

          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              onClick={pick}
              disabled={busy}
              className="pk-btn pk-primary-on-black"
              data-testid="photo-gate-choose"
              style={{ width: '100%', height: 56, fontSize: 12, fontWeight: 800, letterSpacing: '.16em', opacity: busy ? 0.6 : 1 }}
            >
              {busy ? 'ADDING…' : 'CHOOSE A PHOTO'}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/onboard?next=${encodeURIComponent(here)}`)}
              className="pk-btn pk-text-on-black"
              data-testid="photo-gate-setup"
              style={{ width: '100%', height: 40, fontSize: 11, fontWeight: 700, letterSpacing: '.16em' }}
            >
              SET UP MY WHOLE PAGE
            </button>
            <button
              type="button"
              onClick={() => navigate('/featured')}
              className="pk-btn pk-text-on-black"
              style={{ width: '100%', height: 40, fontSize: 11, fontWeight: 700, letterSpacing: '.16em' }}
            >
              BACK TO EVENTS
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
